import './FilterItem.css';

import clsx from 'clsx';
import { useMemo, Dispatch } from 'react';
import { closeCircleOutline } from 'ionicons/icons';
import { IonIcon, IonItem, IonSelect, IonSelectOption } from '@ionic/react';
import { ReferenceControl, SelectedFilter } from '../../../../../types/types';
import InputLabel from '../../../InputLabel/InputLabel';
import { isValuelessMatchMode } from '../../../../../utils/filters/isValuelessMatchMode';
import GridInput from '../../../GridInput/GridInput';
import Button from '../../../../Button/Button';
import { getFilterMatchModes } from '../../../../../utils/filters/getFilterMatchModes';
import { isMandatoryFilter } from '../../../../../utils/isMandatoryFilter';
import { useRequestDataContext } from '../../../../../hooks/context/useRequestDataContext';

const HIDE_MATCH_MODES_TYPES = ['checkbox'];

const UNSUPPORTED_TYPES = ['boolean'];

interface FilterItemProps {
  item: SelectedFilter;
  setFilterMatchMode: (name: any, datasetId: any, matchMode: any) => void;
  setFilterValue: (name: any, datasetId: any, value: any) => void;
  setFilterError: (name: any, datasetId: any, error: any) => void;
  deleteFilter: (name: any, datasetId: any) => void;
  dataset: any;
  referenceDatasets: any;
  setReferenceDatasets: Dispatch<any>;
  openFullscreenReference: (
    name: string,
    datasetId: string,
    control: ReferenceControl,
    value: string,
    label: string,
    // resolvedFilter forwarded from ReferenceInput's click-time JSONata pass.
    // null sentinel = resolved to nothing (strip raw filter).
    resolvedFilter?: Record<string, any> | null
  ) => void;
}

const FilterItem = ({
  item,
  setFilterMatchMode,
  setFilterValue,
  setFilterError,
  deleteFilter,
  dataset,
  referenceDatasets,
  setReferenceDatasets,
  openFullscreenReference,
}: FilterItemProps) => {
  const requestData = useRequestDataContext();

  const hideMatchModes =
    HIDE_MATCH_MODES_TYPES.includes(item.control?.type) ||
    UNSUPPORTED_TYPES.includes(item.type);
  const isCheckbox = item.control?.type === 'checkbox';

  const headerLabel = useMemo(() => {
    if (isCheckbox) {
      return item.label.split(':')[0] + ':';
    }

    return item.label;
  }, [isCheckbox]);

  const checkboxLabel = useMemo(
    () => (isCheckbox ? item.label.split(':')[1] : undefined),
    [isCheckbox]
  );

  const options = useMemo(
    () => getFilterMatchModes(item),
    [item.type, item.control]
  );

  const dropdownValue = useMemo(
    () => options.find(({ name }) => name === item.matchMode),
    [item.matchMode, options]
  );

  const isMandatory = isMandatoryFilter(dataset, item.name, item.datasetId);

  return (
    <div
      key={item.name}
      className={clsx(
        'detailed-filter-item',
        isCheckbox && 'checkbox-filter-item'
      )}
    >
      <InputLabel hint={item.hint}>{headerLabel}</InputLabel>
      <div className="filter-input-container">
        {!hideMatchModes && (
          <IonItem lines="none" className="matchmode-select-container">
            <IonSelect
              value={dropdownValue?.name}
              onIonChange={(e) =>
                setFilterMatchMode(item.name, item.datasetId, e.detail.value)
              }
              interface="popover"
              interfaceOptions={{
                alignment: 'start',
                className: 'matchmode-select-popover',
              }}
            >
              {options.map(({ name, label }, key) => (
                <IonSelectOption
                  key={name}
                  value={name}
                  className={
                    key !== options.length - 1 ? 'matchmode-border' : ''
                  }
                >
                  {label}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
        )}
        {!isValuelessMatchMode(item.matchMode) && (
          <GridInput
            name={item.name}
            type={item.type}
            value={item.value}
            label={checkboxLabel}
            matchMode={item.matchMode}
            error={item.error}
            setError={(error) =>
              setFilterError(item.name, item.datasetId, error)
            }
            onChange={(value) =>
              setFilterValue(item.name, item.datasetId, value)
            }
            openFullscreenReference={(resolvedFilter) =>
              openFullscreenReference(
                item.name,
                item.datasetId,
                item.control as ReferenceControl,
                item.value as string,
                item.label,
                resolvedFilter
              )
            }
            control={item.control}
            referenceDatasets={referenceDatasets}
            setReferenceDatasets={setReferenceDatasets}
            requestData={requestData}
          />
        )}
        <Button
          fill="clear"
          className="delete-filter-button"
          onClick={() => deleteFilter(item.name, item.datasetId)}
          disabled={isMandatory}
        >
          <IonIcon icon={closeCircleOutline} />
        </Button>
      </div>
    </div>
  );
};

export default FilterItem;
